import { useMemo } from 'react'
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
} from 'chart.js'
import { Bar } from 'react-chartjs-2'
import type { FindingEntry } from '@/api/types'
import { getDaysUntilExpiry } from '@/utils/certUtils'

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip)

export function CertExpiryChips({ findings }: { findings: FindingEntry[] }) {
  const buckets = useMemo(() => {
    const b = { expired: 0, soon30: 0, soon90: 0, valid: 0 }
    for (const f of findings) {
      const d = getDaysUntilExpiry(f)
      if (d === null) continue
      if (d <= 0) b.expired++
      else if (d <= 30) b.soon30++
      else if (d <= 90) b.soon90++
      else b.valid++
    }
    return b
  }, [findings])

  const total = buckets.expired + buckets.soon30 + buckets.soon90 + buckets.valid
  if (total === 0) return null

  const chips = [
    { label: 'Expired',  count: buckets.expired, bg: 'bg-error/10 dark:bg-error/15',     text: 'text-error',   border: 'border-error/30',   icon: 'cancel' },
    { label: '<30 days', count: buckets.soon30,  bg: 'bg-warning/10 dark:bg-warning/15', text: 'text-warning', border: 'border-warning/30', icon: 'schedule' },
    { label: '<90 days', count: buckets.soon90,  bg: 'bg-yellow-500/10',                text: 'text-yellow-600 dark:text-yellow-400', border: 'border-yellow-500/30', icon: 'hourglass_bottom' },
    { label: 'Valid',    count: buckets.valid,   bg: 'bg-success/10 dark:bg-success/15', text: 'text-success', border: 'border-success/30', icon: 'verified' },
  ]
  return (
    <div className="flex flex-wrap gap-3">
      {chips.map((c) => (
        <div key={c.label} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border ${c.bg} ${c.border}`}>
          <span className={`material-icons text-lg ${c.text}`}>{c.icon}</span>
          <div>
            <p className={`text-lg font-semibold leading-none ${c.text}`}>{c.count}</p>
            <p className="text-xs text-subtle-light dark:text-subtle-dark mt-0.5">{c.label}</p>
          </div>
          <p className="text-xs text-subtle-light dark:text-subtle-dark ml-1">
            ({Math.round((c.count / total) * 100)}%)
          </p>
        </div>
      ))}
    </div>
  )
}

export function CertExpiryBarChart({ findings, isDark }: { findings: FindingEntry[]; isDark: boolean }) {
  const buckets = useMemo(() => {
    const b = { expired: 0, soon30: 0, soon90: 0, valid: 0 }
    for (const f of findings) {
      const d = getDaysUntilExpiry(f)
      if (d === null) continue
      if (d <= 0) b.expired++
      else if (d <= 30) b.soon30++
      else if (d <= 90) b.soon90++
      else b.valid++
    }
    return b
  }, [findings])

  const total = buckets.expired + buckets.soon30 + buckets.soon90 + buckets.valid
  if (total === 0) return null

  const textSubtle = isDark ? '#7a9ab8' : '#4d6070'
  const gridLine   = isDark ? 'rgba(30,44,62,0.8)' : 'rgba(221,227,235,0.7)'

  const data = {
    labels: ['Expired', '<30 days', '<90 days', 'Valid'],
    datasets: [{
      label: 'Certificates',
      data: [buckets.expired, buckets.soon30, buckets.soon90, buckets.valid],
      backgroundColor: ['rgba(194,34,33,0.75)', 'rgba(234,179,8,0.75)', 'rgba(234,179,8,0.40)', 'rgba(34,197,94,0.70)'],
      borderRadius: 4,
    }],
  }
  const options = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { mode: 'index' as const, intersect: false } },
    scales: {
      x: { beginAtZero: true, grid: { color: gridLine }, ticks: { color: textSubtle, precision: 0, font: { size: 11 } } },
      y: { grid: { display: false }, ticks: { color: textSubtle, font: { size: 11 } } },
    },
  }
  return (
    <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] p-4 rounded-xl shadow-ambient dark:shadow-none border border-border-light dark:border-border-dark">
      <h3 className="text-sm font-medium text-text-light dark:text-text-dark mb-3">Expiry Distribution</h3>
      <div className="h-32"><Bar data={data} options={options} /></div>
    </div>
  )
}

export function CertCaHierarchy({ findings }: { findings: FindingEntry[] }) {
  const grouped = useMemo(() => {
    const map = new Map<string, FindingEntry[]>()
    for (const f of findings) {
      const issuer = f.crypto_properties?.certificateProperties?.issuerName ?? 'Unknown CA'
      const arr = map.get(issuer) ?? []
      arr.push(f)
      map.set(issuer, arr)
    }
    return Array.from(map.entries()).sort((a, b) => b[1].length - a[1].length)
  }, [findings])

  if (grouped.length === 0) return null

  return (
    <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] p-4 rounded-xl shadow-ambient dark:shadow-none border border-border-light dark:border-border-dark">
      <h3 className="text-sm font-medium text-text-light dark:text-text-dark mb-3">CA Hierarchy</h3>
      <div className="space-y-1">
        {grouped.map(([issuer, certs]) => (
          <details key={issuer} className="group">
            <summary className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer hover:bg-background-light dark:hover:bg-background-dark list-none">
              <span className="material-icons text-base text-subtle-light dark:text-subtle-dark group-open:rotate-90 transition-transform">chevron_right</span>
              <span className="material-icons text-base text-primary dark:text-secondary">account_balance</span>
              <span className="text-sm font-medium text-text-light dark:text-text-dark flex-1 truncate">{issuer}</span>
              <span className="text-xs text-subtle-light dark:text-subtle-dark shrink-0">{certs.length} cert{certs.length !== 1 ? 's' : ''}</span>
            </summary>
            <ul className="ml-10 mt-1 space-y-0.5">
              {certs.map((c) => {
                const days = getDaysUntilExpiry(c)
                const isExpired = days !== null && days <= 0
                const isSoon   = days !== null && days > 0 && days <= 30
                return (
                  <li key={c.id} className="flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-background-light dark:hover:bg-background-dark">
                    <span className={`material-icons text-sm ${isExpired ? 'text-error' : isSoon ? 'text-warning' : 'text-success'}`}>
                      {isExpired ? 'cancel' : isSoon ? 'schedule' : 'verified'}
                    </span>
                    <span className="text-sm text-text-light dark:text-text-dark truncate flex-1">
                      {c.crypto_properties?.certificateProperties?.subjectName ?? c.name}
                    </span>
                    {days !== null && (
                      <span className={`text-xs shrink-0 ${isExpired ? 'text-error' : isSoon ? 'text-warning' : 'text-subtle-light dark:text-subtle-dark'}`}>
                        {isExpired ? `expired ${Math.abs(days)}d ago` : `${days}d`}
                      </span>
                    )}
                  </li>
                )
              })}
            </ul>
          </details>
        ))}
      </div>
    </div>
  )
}
